import theme from 'config/theme';
import {Icon} from 'interface/base/Icon';
import Typography from 'interface/base/Typography';
import {Pressable, StyleSheet} from 'react-native';
import {useToast} from 'react-native-toast-notifications';
import {useSelector} from 'react-redux';
import * as app from 'store/slices/app';
import {ColumnManagerInfoKeys} from 'store/slices/app/types';
import type {FilesItem} from 'store/slices/files/types';
import {AiChatSearchPopup} from './AiChatSearchPopup';

enum ButtonState {
  NoAttachments = 'NoAttachments',
  SelectedFiles = 'SelectedFiles',
  Disabled = 'Disabled',
}

const MAX_ATTACH_FILES = 20;

interface Props {
  scopeFiles: FilesItem[];
  attachFiles: FilesItem[];
  handleAdd: (file: FilesItem) => void;
  handleRemove: (file: FilesItem) => void;
}

export function AiChatFilterAttachButton({
  scopeFiles,
  attachFiles,
  handleAdd,
  handleRemove,
}: Props) {
  const toast = useToast();
  const columnWidth = useSelector(
    app.selectors.getColumnWidth(ColumnManagerInfoKeys.AIChat),
  );
  const showLabel = columnWidth > 450;

  const getButtonState = (): ButtonState => {
    if (Object.values(scopeFiles).length !== 0) {
      return ButtonState.Disabled;
    }
    if (Object.values(attachFiles).length === 0) {
      return ButtonState.NoAttachments;
    }
    return ButtonState.SelectedFiles;
  };

  const buttonState = getButtonState();

  const renderButton = (onPress: () => void) => {
    if (buttonState === ButtonState.Disabled) {
      return <Disabled onPress={onPress} />;
    }
    if (buttonState === ButtonState.SelectedFiles) {
      return <SelectedFiles onPress={onPress} showLabel={showLabel} />;
    }
    return <NoAttachments onPress={onPress} showLabel={showLabel} />;
  };

  const fileList = attachFiles;

  const filterForShowing = (item: FilesItem) => {
    return !fileList.find(f => f.id === item.id);
  };

  const filterForEnabling = (item: FilesItem) => {
    return item.ai?.attach === true;
  };

  const handleAddAttachFiles = (file: FilesItem) => {
    if (fileList.length >= MAX_ATTACH_FILES) {
      toast.show(`You can only attach up to ${MAX_ATTACH_FILES} files`);
      return;
    }
    handleAdd(file);
  };

  return (
    <AiChatSearchPopup
      trigger={onPress => renderButton(onPress)}
      fileList={fileList}
      handleAdd={handleAddAttachFiles}
      handleRemove={handleRemove}
      filterForShowing={filterForShowing}
      filterForEnabling={filterForEnabling}
    />
  );
}

function NoAttachments({
  onPress,
  showLabel,
}: {onPress: () => void; showLabel: boolean}) {
  return (
    <Pressable
      onPress={onPress}
      style={[styles.button, {backgroundColor: theme.colors.neutral.$100}]}>
      <Icon
        name="lucide:paperclip"
        size={18}
        color={theme.colors.neutral.$700}
      />
      {showLabel && (
        <Typography size="xs" color={theme.colors.neutral.$700}>
          Attach Files
        </Typography>
      )}
    </Pressable>
  );
}

function SelectedFiles({
  onPress,
  showLabel,
}: {onPress: () => void; showLabel: boolean}) {
  return (
    <Pressable
      onPress={onPress}
      style={[styles.button, {backgroundColor: theme.colors.neutral.$700}]}>
      <Icon name="lucide:paperclip" size={18} color={theme.colors.neutral.$0} />
      {showLabel && (
        <Typography size="xs" color={theme.colors.neutral.$0}>
          Attached Files
        </Typography>
      )}
    </Pressable>
  );
}

function Disabled({onPress}: {onPress: () => void}) {
  return (
    <Pressable
      onPress={onPress}
      disabled={true}
      style={[styles.button, {backgroundColor: theme.colors.neutral.$100}]}>
      <Icon
        name="lucide:paperclip"
        size={18}
        color={theme.colors.neutral.$300}
      />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  button: {
    borderRadius: 6,
    paddingHorizontal: 8,
    height: 22,
    paddingVertical: 2,
    flexDirection: 'row',
    gap: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
